class BundleBuilder extends HTMLElement {
    constructor() {
        super();
        this.bundleData = JSON.parse(this.querySelector('#bundle-data').innerHTML).sort((a, b) => a.count - b.count);
        this.selectedBundle = this.bundleData[0];
        this.tierFloor = this.bundleData[0].count;
        this.currencySymbol = this.getAttribute('data-currency-symbol');

        // State of products
        this.selectedProducts = []; // { id, image, price }

        this.initElements();

        this._ensureTierStyles();
        this.loadStateFromStorage();
        this.initializeTabStickyListener();
        this.initializeStickyButtonListener();
        this.initializeTabListeners();
        this.restoreCardStates(); // <-- Refresh ke baad card styles restore karo
        this.updateUI(); // Run before initProductPagination so button states have final say
        this.initProductPagination();
        this.initRecipeCards();       // <-- Recipe cards initialize
        this.initRecipeLoadMore();    // <-- Load more for recipe grid
        this.initRecipeVideoPlay();   // <-- Video play buttons
        this.initializeProductCardListeners();
        this.initializeProductSliderListeners();
        this.initializePopupCarousel();
        this.initializeBundleATCListener();

        this.addEventListener('bundle:add',    this.handleBundleAdd.bind(this));
        this.addEventListener('bundle:remove', this.handleBundleRemove.bind(this));

        // Agar user checkout complete kar ke thank_you / order status page par pahunch chuka hai,
        // to purana bundle state clear kar do taake agli dafa fresh bundle start ho.
        this.clearStateIfOrderCompleted();
    }

    /* ============================================================
       STORAGE
    ============================================================ */
    getStorageKey() {
        return `bundleBuilderState:${window.location.pathname}`;
    }

    saveStateToStorage() {
        try {
            const state = {
                selectedBundleCount: this.selectedBundle?.count,
                tierFloor: this.tierFloor,
                selectedProducts: this.selectedProducts || [],
            };
            window.localStorage.setItem(this.getStorageKey(), JSON.stringify(state));
        } catch (e) {
            console.warn('BundleBuilder: unable to save state', e);
        }
    }

    loadStateFromStorage() {
        try {
            const raw = window.localStorage.getItem(this.getStorageKey());
            if (!raw) return;
            const state = JSON.parse(raw);
            if (!state || typeof state !== 'object') return;

            const { selectedBundleCount, selectedProducts, tierFloor } = state;

            if (selectedBundleCount) {
                const matchedBundle = this.bundleData.find(b => b.count === selectedBundleCount);
                if (matchedBundle) this.selectedBundle = matchedBundle;
            }

            if (Array.isArray(selectedProducts)) {
                this.selectedProducts = selectedProducts.filter(
                    p => p && p.id && p.image && typeof p.price !== 'undefined'
                );
            }
            // The floor is the tile the shopper tapped (older saved states only had the pack): the pack then follows the item count.
            this.tierFloor = this.bundleData.some(b => b.count === tierFloor) ? tierFloor : (this.selectedBundle.count || this.bundleData[0].count);
            this.applyTierForCount();
        } catch (e) {
            console.warn('BundleBuilder: unable to load state', e);
        }
    }

    clearStateFromStorage() {
        try {
            window.localStorage.removeItem(this.getStorageKey());
        } catch (e) {
            console.warn('BundleBuilder: unable to clear state', e);
        }
    }

    // Order complete hone ke baad (thank_you / order status page) state clear karo.
    // Checkout button click par state clear NAHI hoti — is se user checkout se
    // "Back" aaye to uska bundle waisa hi restore ho jata hai jaisa chhoda tha.
    clearStateIfOrderCompleted() {
        try {
            const path = window.location.pathname;
            const isOrderCompletePage = path.includes('/thank_you') || path.includes('/orders/');
            if (isOrderCompletePage) {
                this.clearStateFromStorage();
            }
        } catch (e) {
            console.warn('BundleBuilder: unable to check order completion state', e);
        }
    }

    /* ============================================================
       RESTORE CARD STATES (page refresh ke baad visual styles lagao)
    ============================================================ */
    restoreCardStates() {
        // Har selected product ka count nikalo
        const countMap = {};
        this.selectedProducts.forEach(p => {
            countMap[p.id] = (countMap[p.id] || 0) + 1;
        });

        // Saare product cards pe styles apply karo
        [...this.querySelectorAll('.product-card')].forEach(card => {
            const addBtn = card.querySelector('.add-btn');
            if (!addBtn) return;

            const variantId = addBtn.dataset.id;
            if (variantId && countMap[variantId]) {
                card.classList.add('in-bundle');
                card.setAttribute('data-bundle-count', countMap[variantId]);

                // In-bundle cards ko grid mein upar lao
                const parentGrid = card.closest('.product-grid');
                if (parentGrid && parentGrid.firstChild !== card) {
                    parentGrid.prepend(card);
                }
            }
        });
    }

    /* ============================================================
       MONEY
    ============================================================ */
    formatMoney(amount) {
        return `${this.currencySymbol}${(amount / 100).toFixed(2)}`;
    }

    localizeDollarsToCurrency(amount) {
        return amount * window.Shopify.currency.rate;
    }

    /* ============================================================
       INIT ELEMENTS
    ============================================================ */
    initElements() {
        // Tabs
        this.tabsSection  = this.querySelector('#tabs-section');
        this.tabs         = this.tabsSection.querySelector('.tabs');
        this.tabContents  = [...this.querySelectorAll('.tab-content')];
        this.tabButtons   = [...this.tabs.querySelectorAll('.tab-btn')];

        // Products
        this.productSliders = this.querySelectorAll('.product-slider');

        // Bundles
        this.bundleOptions  = this.querySelector('.bundle-options');
        this.progressBars   = this.querySelectorAll('.progress-fill');

        // Progress
        this.progressSavingsAmount = this.querySelector('.progress-savings-amount');
        this.progressStatus        = this.querySelector('.progress-status');

        // Build bundle options from data
        this.bundleData.forEach(bundle => {
            const bundleOption = this.querySelector('#bundle-template').content.cloneNode(true).querySelector('div');
            bundleOption.querySelector('.bundle-count').textContent = bundle.count;
            let price = this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100));
            price = price.replace(/\s/g, '');
            bundleOption.querySelector('.bundle-discount').textContent = `${price} off`;
            bundleOption.dataset.count = bundle.count;
            this.bundleOptions.appendChild(bundleOption);

            bundleOption.addEventListener('click', () => {
                this.onBundleChange(bundle.count);
            });
        });

        // Price
        this.originalPrice   = this.querySelector('[data-original-price]');
        this.discountedPrice = this.querySelector('[data-discounted-price]');
        this.discount        = this.querySelector('[data-discount]');

        // ATC buttons
        this.bundleATCButton       = this.querySelector('.btn_cr:not(.sticky-btn)');
        this.stickyButtonContainer = this.querySelector('.sticky-btn-container');
        this.stickyButton          = this.stickyButtonContainer?.querySelector('.sticky-btn');

        // Sticky bar elements
        this.stickyItemsAdded     = this.stickyButtonContainer?.querySelector('.sticky-items-added');
        this.stickyItemsRemaining = this.stickyButtonContainer?.querySelector('.sticky-items-remaining');
        this.stickyProgressFill   = this.stickyButtonContainer?.querySelector('.sticky-progress-fill');
        this.stickySavingsAmount  = this.stickyButtonContainer?.querySelector('.sticky-savings-amount');
        this.stickyStatus         = this.stickyButtonContainer?.querySelector('.sticky-status');
        this.stickyStatusCount    = this.stickyButtonContainer?.querySelector('.sticky-status-count');
    }

    /* ============================================================
       RECIPE CARDS — initialize swatches + onProductChange
    ============================================================ */
    initRecipeCards() {
        const recipeCards = this.querySelectorAll('.recipe-card.product-card');

        recipeCards.forEach(card => {
            const firstColor = card.querySelector('.color-swatch');
            if (firstColor) firstColor.classList.add('active');

            const firstSize = card.querySelector('.size-swatch');
            if (firstSize) firstSize.classList.add('active');

            this.onProductChange(card);
        });
    }

    /* ============================================================
       RECIPE LOAD MORE — show 4 at a time
    ============================================================ */
    initRecipeLoadMore() {
        const loadMoreBtn = this.querySelector('.popular-recipes-load-more');
        if (!loadMoreBtn) return;

        loadMoreBtn.addEventListener('click', () => {
            const hiddenCards = [...this.querySelectorAll('.recipe-card.recipe-hidden')];
            let shown = 0;

            hiddenCards.forEach(card => {
                if (shown < 4) {
                    card.classList.remove('recipe-hidden');
                    card.style.animation = 'recipeCardFadeIn 0.3s ease both';
                    shown++;
                }
            });

            if (!this.querySelector('.recipe-card.recipe-hidden')) {
                loadMoreBtn.closest('.popular-recipes-load-more-wrap').style.display = 'none';
            }
        });
    }

    /* ============================================================
       RECIPE VIDEO PLAY / PAUSE
    ============================================================ */
    initRecipeVideoPlay() {
        this.querySelectorAll('.recipe-card__video').forEach(wrap => {
            const btn   = wrap.querySelector('.recipe-play-btn');
            const video = wrap.querySelector('.recipe-video');
            if (!btn || !video) return;

            btn.addEventListener('click', () => {
                if (video.paused) {
                    video.play();
                    btn.style.opacity = '0';
                } else {
                    video.pause();
                    btn.style.opacity = '1';
                }
            });

            video.addEventListener('click', () => {
                if (!video.paused) {
                    video.pause();
                    btn.style.opacity = '1';
                }
            });

            video.addEventListener('ended', () => {
                btn.style.opacity = '1';
            });
        });
    }

    /* ============================================================
       BUNDLE ATC
    ============================================================ */
    initializeBundleATCListener() {
        this.bundleATCButton.addEventListener('click', () => {
            const itemAddStrings      = this.selectedProducts.map(product => `${product.id}:1`);
            const checkoutParamString = `${itemAddStrings.join(',')}?discount=${this.selectedBundle.discount_code}`;
            const permalink           = `/cart/${checkoutParamString}`;
            // FIX: clearStateFromStorage() yahan se hata diya gaya hai.
            // Pehle ye yahan call hota tha aur redirect se pehle hi localStorage khali kar deta tha,
            // isliye jab user checkout se "Back" aata tha to bundle empty dikhta tha (products remove ho jate the).
            // Ab state clear sirf order complete hone ke baad hoti hai (clearStateIfOrderCompleted mein),
            // is liye back aane par bundle wapis restore ho jayega.
            window.location.href = permalink;
        });
    }

    /* ============================================================
       UPDATE UI
    ============================================================ */
    updateUI() {
        const progressBarWidth = (this.selectedProducts.length / this.selectedBundle.count) * 100;
        this.progressBars.forEach(pb => pb.style.width = `${progressBarWidth}%`);

        const itemsAdded       = this.selectedProducts.length;
        const itemsRemaining   = this.selectedBundle.count - itemsAdded;
        const isBundleComplete = itemsAdded === this.selectedBundle.count;
        const nextTier = this.bundleData.find(b => b.count > this.selectedBundle.count) || null;
        const isBoxFull = isBundleComplete && !nextTier;
        const upHint = isBundleComplete && nextTier ? `<span class="fz-bb-up">Add 1 more and you move up to the ${this.packLabel(nextTier)} (${this.discountLabel(nextTier)} off)</span>` : '';

        if (this.progressSavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.progressSavingsAmount.textContent = this.formatMoney(savingsAmount);
        }

        if (this.progressStatus) {
            if (isBundleComplete) {
                this.progressStatus.innerHTML = 'Bundle complete! Ready to checkout' + upHint;
                this.progressStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round(progressBarWidth);
                this.progressStatus.innerHTML = `Add <span class="progress-status-count">${itemsRemaining}</span> more to complete your bundle <span class="clr_discount">(${progressPercent}% done)</span>`;
                this.progressStatus.classList.remove('complete');
            }
        }

        // Curve images
        const placeholderImage = this.querySelector('template[data-placeholder-image]').content.cloneNode(true).querySelector('img');
        const imagesContainer  = this.querySelector('.curve-images');
        imagesContainer.innerHTML = '';

        this.selectedProducts.forEach(product => {
            const img = document.createElement('img');
            img.src   = product.image;

            const crossButton = document.createElement('button');
            crossButton.classList.add('cross-btn');
            crossButton.innerHTML = '×';
            crossButton.addEventListener('click', () => {
                this.dispatchEvent(new CustomEvent('bundle:remove', {
                    detail: { variantId: product.id }
                }));
            });

            const imgContainer = document.createElement('div');
            imgContainer.classList.add('img-container');
            imgContainer.appendChild(img);
            imgContainer.appendChild(crossButton);
            imagesContainer.appendChild(imgContainer);
        });

        for (let i = this.selectedProducts.length; i < this.selectedBundle.count; i++) {
            imagesContainer.appendChild(placeholderImage.cloneNode(true));
        }

        // Active bundle option
        this.bundleOptions.querySelectorAll('.bundle').forEach(b => b.classList.remove('active'));
        this.bundleOptions.querySelector(`[data-count="${this.selectedBundle.count}"]`).classList.add('active');

        // Price
        const totalPrice = this.selectedProducts.reduce((acc, product) => acc + +product.price, 0);
        this.discount.textContent = this.formatMoney(this.localizeDollarsToCurrency(this.selectedBundle.discount * 100));

        if (totalPrice === 0) {
            this.originalPrice.textContent  = '';
            this.discountedPrice.textContent = '';
        } else {
            this.originalPrice.textContent = this.formatMoney(totalPrice);
            const bundleDiscountLocalized  = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.discountedPrice.textContent = this.formatMoney(totalPrice - bundleDiscountLocalized);
        }

        // ATC button enable / disable
        if (isBundleComplete) {
            this.bundleATCButton.disabled = false;
            this.bundleATCButton.classList.remove('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = false;
                this.stickyButton.classList.remove('disabled');
            }
            if (isBoxFull) {
                this.querySelectorAll('.add-btn').forEach(btn => {
                    btn.disabled = true;
                    btn.classList.add('disabled', 'is-full');
                    btn.textContent = 'Box is full';
                });
            } else {
                // A full pack that can still grow keeps Add alive: the next item upgrades the pack.
                this.querySelectorAll('.add-btn').forEach(btn => btn.classList.remove('is-full'));
                this.querySelectorAll('.product-card').forEach(productCard => {
                    this.onProductChange(productCard);
                });
            }
        } else {
            this.bundleATCButton.disabled = true;
            this.bundleATCButton.classList.add('disabled');
            if (this.stickyButton) {
                this.stickyButton.disabled = true;
                this.stickyButton.classList.add('disabled');
            }
            this.querySelectorAll('.add-btn').forEach(btn => btn.classList.remove('is-full'));
            this.querySelectorAll('.product-card').forEach(productCard => {
                this.onProductChange(productCard);
            });
        }

        // Sticky bar
        if (this.stickyItemsAdded) this.stickyItemsAdded.textContent = `${itemsAdded} item${itemsAdded !== 1 ? 's' : ''} added`;

        if (this.stickyItemsRemaining) {
            if (isBundleComplete) {
                this.stickyItemsRemaining.textContent = 'Bundle complete!';
                this.stickyItemsRemaining.classList.add('complete');
            } else {
                this.stickyItemsRemaining.textContent = `${itemsRemaining} remaining`;
                this.stickyItemsRemaining.classList.remove('complete');
            }
        }

        if (this.stickyProgressFill) {
            this.stickyProgressFill.style.width = `${(itemsAdded / this.selectedBundle.count) * 100}%`;
        }

        if (this.stickySavingsAmount) {
            const savingsAmount = this.localizeDollarsToCurrency(this.selectedBundle.discount * 100);
            this.stickySavingsAmount.textContent = this.formatMoney(savingsAmount);
        }

        if (this.stickyStatus) {
            if (isBundleComplete) {
                this.stickyStatus.textContent = 'Bundle complete! Ready to checkout';
                this.stickyStatus.classList.add('complete');
            } else {
                const progressPercent = Math.round((itemsAdded / this.selectedBundle.count) * 100);
                this.stickyStatus.innerHTML = `Add <span class="sticky-status-count">${itemsRemaining}</span> more to complete your bundle <span class="clr_discount">(${progressPercent}% done)</span>`;
                this.stickyStatus.classList.remove('complete');
            }
        }

        // Sticky bar visibility
        if (this.stickyButtonContainer) {
            if (itemsAdded > 0) {
                this.stickyButtonContainer.classList.add('mobile-visible');
            } else {
                this.stickyButtonContainer.classList.remove('mobile-visible');
            }
        }

        this.saveStateToStorage();
    }

    /* ============================================================
       BUNDLE CHANGE
    ============================================================ */
    onBundleChange(count) {
        this.tierFloor = count;
        this.hideTierToast();
        if (count === this.selectedBundle.count) return;

        this.selectedBundle = this.bundleData.find(bundle => bundle.count === count);

        if (this.selectedProducts.length > count) {
            this.selectedProducts = this.selectedProducts.slice(0, count);
        }

        this.updateUI();
    }

    /* ============================================================
       BUNDLE ADD / REMOVE EVENTS
    ============================================================ */
    handleBundleAdd(e) {
        const { variantId, count, image, price } = e.detail;
        const postTotal = this.selectedProducts.length + count;

        if (postTotal > this.maxBundleCount()) {
            this.showTierToast(`Your box is full: the <b>${this.packLabel(this.selectedBundle)}</b> holds ${this.maxBundleCount()} items. Remove one to swap.`);
            return;
        }

        for (let i = 0; i < count; i++) {
            this.selectedProducts.push({ id: variantId, image, price });
        }

        const upgradedFrom = this.applyTierForCount();

        // Saare tabs + recipe grid mein us variant ke SAARE matching cards dhundo
        const allMatchingCards = [...this.querySelectorAll('.product-card')].filter(card => {
            return card.querySelector('.add-btn')?.dataset.id == variantId;
        });

        const qty = this.selectedProducts.filter(p => String(p.id) === String(variantId)).length;

        allMatchingCards.forEach(card => {
            card.classList.add('in-bundle');
            card.setAttribute('data-bundle-count', qty);

            // Har grid mein pehli position pe lao
            const parentGrid = card.closest('.product-grid');
            if (parentGrid && parentGrid.firstChild !== card) {
                parentGrid.prepend(card);
            }
        });

        this.updateUI();

        if (upgradedFrom) {
            const b = this.selectedBundle, remaining = b.count - this.selectedProducts.length;
            this.showTierToast(
                `Moved up to the <b>${this.packLabel(b)}</b> · <b>${this.discountLabel(b)} off</b>. ` + (remaining > 0 ? `Add ${remaining} more to complete it.` : 'Bundle complete!'),
                () => { for (let i = 0; i < count; i++) this.dispatchEvent(new CustomEvent('bundle:remove', { detail: { variantId, silent: true } })); }
            );
        }
    }

    handleBundleRemove(e) {
        const variantId = e.detail.variantId;
        let indexToRemove = -1;

        for (let i = this.selectedProducts.length - 1; i >= 0; i--) {
            if (this.selectedProducts[i].id === variantId) {
                indexToRemove = i;
                break;
            }
        }

        if (indexToRemove !== -1) {
            this.selectedProducts.splice(indexToRemove, 1);

            const downgradedFrom = this.applyTierForCount();
            if (downgradedFrom && !e.detail.silent) {
                this.showTierToast(`Back to the <b>${this.packLabel(this.selectedBundle)}</b> · <b>${this.discountLabel(this.selectedBundle)} off</b>`);
            }

            // Remaining count check karo
            const remaining = this.selectedProducts.filter(
                p => String(p.id) === String(variantId)
            ).length;

            // Saare tabs mein matching cards update karo
            const allMatchingCards = [...this.querySelectorAll('.product-card')].filter(card => {
                return card.querySelector('.add-btn')?.dataset.id == variantId;
            });

            allMatchingCards.forEach(card => {
                if (remaining === 0) {
                    card.classList.remove('in-bundle');
                    card.removeAttribute('data-bundle-count');

                    // Baaki in-bundle cards ko top pe rakhna continue karo
                    const parentGrid = card.closest('.product-grid');
                    if (parentGrid) {
                        const inBundleCards = [...parentGrid.querySelectorAll('.product-card.in-bundle')];
                        inBundleCards.reverse().forEach(c => parentGrid.prepend(c));
                    }
                } else {
                    card.setAttribute('data-bundle-count', remaining);
                }
            });

            this.updateUI();
        }
    }

    /* ============================================================
       STICKY TAB LISTENER
    ============================================================ */
    initializeTabStickyListener() {
        const tabsOffset = this.tabsSection.offsetTop;

        window.addEventListener('scroll', () => {
            if (window.scrollY >= tabsOffset) {
                this.tabs.classList.add('is-sticky');
            } else {
                this.tabs.classList.remove('is-sticky');
            }
        });
    }

    /* ============================================================
       STICKY BUTTON LISTENER
    ============================================================ */
    initializeStickyButtonListener() {
        if (!this.bundleATCButton || !this.stickyButtonContainer || !this.stickyButton) return;

        this.stickyButton.addEventListener('click', () => {
            this.bundleATCButton.click();
        });
    }

    /* ============================================================
       TAB LISTENERS
    ============================================================ */
    initializeTabListeners() {
        this.tabButtons.forEach(button => {
            button.addEventListener('click', () => {
                this.querySelectorAll('.tab-btn, .tab-content').forEach(el => el.classList.remove('active'));
                button.classList.add('active');

                const activeTab = this.querySelector(`#${button.dataset.tab}`);
                activeTab.classList.add('active');

                const products       = activeTab.querySelectorAll('.product-card');
                const loadBtn        = activeTab.querySelector('.loadmore-btn');
                const initialVisible = 8;
                this.toggleProductVisibility(products, initialVisible, loadBtn);

                const headerOffset = window.innerWidth < 768 ? 80 : 120;
                const scrollTarget = activeTab.getBoundingClientRect().top + window.pageYOffset - headerOffset - 60;
                window.scrollTo({ top: Math.max(0, scrollTarget), behavior: 'smooth' });
            });
        });

        // Pehli tab default active — scroll ke bina
        if (this.tabButtons.length) {
            const firstBtn = this.tabButtons[0];
            firstBtn.classList.add('active');
            const firstTab = this.querySelector(`#${firstBtn.dataset.tab}`);
            if (firstTab) {
                firstTab.classList.add('active');
                const products = firstTab.querySelectorAll('.product-card');
                const loadBtn  = firstTab.querySelector('.loadmore-btn');
                this.toggleProductVisibility(products, 8, loadBtn);
            }
        }
    }

    /* ============================================================
       PRODUCT SLIDER
    ============================================================ */
    moveSliderTo(slider, index) {
        const track = slider.querySelector('.slider-track');
        track.style.transform = `translateX(-${index * 100}%)`;
    }

    initializeProductSliderListeners() {
        this.productSliders.forEach(slider => {
            const slides = slider.querySelectorAll('.slide');
            const prev   = slider.querySelector('.left');
            const next   = slider.querySelector('.right');
            if (!slides.length || !prev || !next) return;

            let index = 0;

            next.addEventListener('click', e => {
                e.preventDefault();
                index = (index + 1) % slides.length;
                this.moveSliderTo(slider, index);
            });

            prev.addEventListener('click', e => {
                e.preventDefault();
                index = (index - 1 + slides.length) % slides.length;
                this.moveSliderTo(slider, index);
            });
        });
    }

    /* ============================================================
       PRODUCT PAGINATION (tab cards only)
    ============================================================ */
    toggleProductVisibility(products, visibleCount, loadBtn) {
        products.forEach((card, index) => {
            if (index < visibleCount) card.classList.add('visible');
        });

        if (visibleCount >= products.length) loadBtn.classList.add('hide');
    }

    initProductPagination() {
        for (const tab of this.tabContents) {
            const products = tab.querySelectorAll('.product-card');

            products.forEach(product => {
                const firstColor = product.querySelector('.color-swatch');
                if (firstColor) firstColor.classList.add('active');

                const firstSize = product.querySelector('.size-swatch');
                if (firstSize) firstSize.classList.add('active');

                this.onProductChange(product);
            });

            const loadBtn    = tab.querySelector('.loadmore-btn');
            let visibleCount = 8;

            this.toggleProductVisibility(products, visibleCount, loadBtn);

            loadBtn.addEventListener('click', e => {
                e.preventDefault();
                visibleCount += 8;
                this.toggleProductVisibility(products, visibleCount, loadBtn);
            });
        }
    }

    /* ============================================================
       ON PRODUCT CHANGE
    ============================================================ */
    _ensurePriceStyles() {
        if (document.getElementById('bundle-card-price-styles')) return;

        const style = document.createElement('style');
        style.id = 'bundle-card-price-styles';
        style.textContent =
            // Top "Choose What Goes In Your Bundle" cards: only ~118px of usable
            // width around a 900px window, so the type is a step smaller there.
            '.card-bottom > p{display:flex;justify-content:center;align-items:baseline;gap:2px;flex-wrap:wrap;}' +
            '.card-bottom > p .card-price__sale{font-size:13.5px;font-weight:700;color:#121212;}' +
            '.card-bottom > p .card-price__was{font-size:10px;color:#8a8a8a;text-decoration:line-through;}' +
            '.card-bottom > p .card-price__badge{padding:1px 3px;border-radius:999px;background:#FF6A00;' +
            'color:#fff;font-size:8px;font-weight:700;white-space:nowrap;align-self:center;}' +
            // Recipe cards are much wider (~245px), so they get the full-size treatment.
            '.recipe-card .card-price{display:flex;justify-content:center;align-items:baseline;gap:3px;' +
            'flex-wrap:wrap;margin:12px 0 0;}' +
            '.recipe-card .card-price__sale{font-size:15px;font-weight:700;color:#121212;}' +
            '.recipe-card .card-price__was{font-size:11.5px;color:#8a8a8a;text-decoration:line-through;}' +
            '.recipe-card .card-price__badge{padding:1px 4px;border-radius:999px;background:#FF6A00;' +
            'color:#fff;font-size:9px;font-weight:700;white-space:nowrap;align-self:center;}' +
            // Sold-out options: visible but clearly not buyable.
            '.swatch-sold-out{opacity:.35;}' +
            '.size-swatch.swatch-sold-out{text-decoration:line-through;}' +
            '.color-swatch.swatch-sold-out{filter:grayscale(1);}';
        document.head.appendChild(style);
    }

    // The store's decimal separator, read once from a price Liquid rendered.
    // EU shows "\u20ac15,95" but its <html lang> is "en", so Intl cannot be trusted here.
    _detectDecimalSeparator() {
        if (this._decimalSeparator !== undefined) return this._decimalSeparator;

        let separator = '.';
        for (const node of document.querySelectorAll('.card-bottom > p')) {
            const text = node.textContent;
            if (/\d,\d{2}(?!\d)/.test(text)) { separator = ','; break; }
            if (/\d\.\d{2}(?!\d)/.test(text)) { separator = '.'; break; }
        }

        this._decimalSeparator = separator;
        return separator;
    }

    _formatCardMoney(cents) {
        const formatted = this.formatMoney(cents);
        return this._detectDecimalSeparator() === ',' ? formatted.replace(/\.(\d{2})$/, ',$1') : formatted;
    }

    // Bundle cards ship a price element in the Liquid; recipe cards don't, so we
    // create one directly above the qty / Add to Bundle row.
    _priceElementFor(productCard) {
        const existing = productCard.querySelector('.card-bottom .card-price')
            || productCard.querySelector('.card-bottom > p');
        if (existing) return existing;

        const addRow = productCard.querySelector('.qty_add_btn_flex');
        if (!addRow) return null;

        let created = productCard.querySelector('p.card-price');
        if (!created) {
            created = document.createElement('p');
            created.className = 'card-price';
            addRow.before(created);
        }
        return created;
    }

    // Grey out the size/colour options that are sold out for the current selection,
    // so a shopper can see the dead ends instead of clicking into them.
    updateSoldOutSwatches(productCard, variants) {
        if (!productCard || !Array.isArray(variants) || !variants.length) return;

        const activeSize  = productCard.querySelector('.size-swatch.active');
        const activeColor = productCard.querySelector('.color-swatch.active');
        const sizeIndex   = activeSize?.dataset?.optionIndex  || '';
        const colorIndex  = activeColor?.dataset?.optionIndex || '';

        const buyable = (swatch, ownIndex, otherIndex, otherValue) =>
            variants.some(variant =>
                variant.available &&
                variant['option' + ownIndex] === swatch.dataset.value &&
                (!otherIndex || variant['option' + otherIndex] === otherValue)
            );

        productCard.querySelectorAll('.size-swatch').forEach(swatch => {
            const index = swatch.dataset.optionIndex || sizeIndex;
            if (!index) return;
            swatch.classList.toggle('swatch-sold-out',
                !buyable(swatch, index, colorIndex, activeColor?.dataset?.value));
        });

        productCard.querySelectorAll('.color-swatch').forEach(swatch => {
            const index = swatch.dataset.optionIndex || colorIndex;
            if (!index) return;
            swatch.classList.toggle('swatch-sold-out',
                !buyable(swatch, index, sizeIndex, activeSize?.dataset?.value));
        });
    }

    updateCardPrice(productCard, variant) {
        if (!productCard) return;

        const priceEl = this._priceElementFor(productCard);
        if (!priceEl) return;

        if (!variant || variant.price == null) {
            if (priceEl.classList.contains('card-price')) priceEl.textContent = '';
            return;
        }

        this._detectDecimalSeparator();
        this._ensurePriceStyles();

        const compareAt = variant.compare_at_price;

        if (!(compareAt && compareAt > variant.price)) {
            priceEl.textContent = this._formatCardMoney(variant.price);
            return;
        }

        const percent = Math.round((1 - variant.price / compareAt) * 100);

        priceEl.innerHTML =
            '<span class="card-price__sale">' + this._formatCardMoney(variant.price) + '</span>' +
            '<s class="card-price__was">' + this._formatCardMoney(compareAt) + '</s>' +
            '<span class="card-price__badge">-' + percent + '%</span>';
    }

    // =====================================================================
    // Count-driven pack size (Sep 2026). The tier follows the number of items in the box: adding a 6th item to a full
    // 5-pack moves the shopper up to the 9-pack, removing it drops them back. The tile they tapped is a floor, never a cap.
    // =====================================================================
    maxBundleCount() {
        return this.bundleData[this.bundleData.length - 1].count;
    }

    tierForCount(count) {
        const floor = this.tierFloor || this.bundleData[0].count;
        const fit = this.bundleData.find(b => b.count >= count);
        const want = Math.max(floor, fit ? fit.count : this.maxBundleCount());
        return this.bundleData.find(b => b.count === want) || this.bundleData[this.bundleData.length - 1];
    }

    // Re-derive the pack from the item count. Returns the previous pack when the tier changed, otherwise null.
    applyTierForCount() {
        const before = this.selectedBundle;
        const tier = this.tierForCount(this.selectedProducts.length);
        if (tier.count === before.count) return null;
        this.selectedBundle = tier;
        return before;
    }

    // Discount already banked: the biggest pack the shopper has completely filled (0 under the first tier).
    earnedDiscount() {
        const n = this.selectedProducts.length;
        let earned = 0;
        this.bundleData.forEach(b => { if (n >= b.count) earned = b.discount; });
        return earned;
    }

    packLabel(bundle) {
        return `${bundle.count}-pack`;
    }

    discountLabel(bundle) {
        return this.formatMoney(this.localizeDollarsToCurrency(bundle.discount * 100)).replace(/\s/g, '');
    }

    showTierToast(html, undo) {
        this._ensureTierStyles();
        let toast = document.querySelector('.fz-bb-toast');
        if (!toast) { toast = document.createElement('div'); toast.className = 'fz-bb-toast'; document.body.appendChild(toast); }
        clearTimeout(this._toastTimer);
        toast.innerHTML = `<span class="fz-bb-toast__txt">${html}</span>` + (undo ? '<button type="button" class="fz-bb-toast__undo">Undo</button>' : '') + '<button type="button" class="fz-bb-toast__x" aria-label="Dismiss">&times;</button>';
        const hide = () => { toast.classList.remove('is-on'); };
        toast.querySelector('.fz-bb-toast__x').addEventListener('click', hide);
        const u = toast.querySelector('.fz-bb-toast__undo');
        if (u) u.addEventListener('click', () => { hide(); undo(); });
        requestAnimationFrame(() => toast.classList.add('is-on'));
        this._toastTimer = setTimeout(hide, 7000);
    }

    hideTierToast() {
        const toast = document.querySelector('.fz-bb-toast');
        if (toast) toast.classList.remove('is-on');
        clearTimeout(this._toastTimer);
    }

    _ensureTierStyles() {
        if (document.getElementById('fz-bb-tier-styles')) return;
        const style = document.createElement('style');
        style.id = 'fz-bb-tier-styles';
        style.textContent = [
            '.fz-bb-toast{position:fixed;left:50%;top:96px;transform:translate(-50%,-12px);z-index:2147483000;display:flex;align-items:center;gap:10px;max-width:min(92vw,460px);padding:12px 12px 12px 16px;background:#111;color:#fff;border-radius:12px;box-shadow:0 10px 30px rgba(0,0,0,.25);font-family:inherit;font-size:14px;line-height:1.35;opacity:0;pointer-events:none;transition:opacity .25s ease,transform .25s ease;box-sizing:border-box;}',
            '.fz-bb-toast.is-on{opacity:1;pointer-events:auto;transform:translate(-50%,0);}',
            '.fz-bb-toast b{color:#FF9A5C;font-weight:800;}',
            '.fz-bb-toast__txt{flex:1;}',
            '.fz-bb-toast__undo{flex:none;background:#F74E00;color:#fff;border:0;border-radius:8px;padding:8px 14px;font-weight:800;font-size:13px;cursor:pointer;font-family:inherit;}',
            '.fz-bb-toast__x{flex:none;background:transparent;color:#fff;border:0;font-size:20px;line-height:1;padding:0 2px;cursor:pointer;opacity:.7;}',
            '@media (max-width:749px){.fz-bb-toast{top:auto;bottom:150px;width:calc(100vw - 24px);font-size:13px;}}',
            '.progress-status .fz-bb-up,.sticky-status .fz-bb-up{display:block;font-size:.85em;font-weight:600;opacity:.75;margin-top:2px;}',
            '.add-btn.is-full{cursor:not-allowed;}',
        ].join('\n');
        document.head.appendChild(style);
    }

    onProductChange(productCard) {
        const self = this;

        function updateATCButton(atcBtn, variant) {
            self.updateCardPrice(productCard, variant);

            if (variant && variant.available) {
                atcBtn.disabled = false;
                atcBtn.classList.remove('disabled');
                atcBtn.textContent   = atcBtn.getAttribute('data-available');
                atcBtn.dataset.id    = variant.id;
                atcBtn.dataset.price = variant.price;
            } else {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent   = atcBtn.getAttribute('data-out-of-stock');
                atcBtn.dataset.id    = '';
                atcBtn.dataset.price = '';
            }
        }

        const activeColorSwatch = productCard.querySelector('.color-swatch.active');
        const activeSizeSwatch  = productCard.querySelector('.size-swatch.active');
        const atcBtn            = productCard.querySelector('.add-btn');

        const scriptTag = productCard.querySelector('script[type="application/json"]');
        if (!scriptTag || !atcBtn) return;

        let productData;
        try {
            productData = JSON.parse(scriptTag.innerHTML);
        } catch (err) {
            console.warn('BundleBuilder: invalid product JSON in recipe card, skipping', err, productCard);
            return;
        }

        // FIX: productData.variants kabhi kabhi null/undefined aa raha tha
        // (jab Liquid product resolve nahi hota — deleted/unavailable product),
        // jis se "Cannot read properties of null (reading 'length')" crash hota tha
        // aur uske baad ke saare initializers (event listeners waghera) run hi nahi hote the.
        // Ab agar variants array na ho, to card ko safely "Select options" / disabled
        // state mein chhod kar return kar dete hain, poore script ko crash kiye bina.
        const variants = Array.isArray(productData?.variants) ? productData.variants : [];

        if (!variants.length) {
            atcBtn.disabled = true;
            atcBtn.classList.add('disabled');
            atcBtn.textContent = atcBtn.getAttribute('data-out-of-stock') || 'Unavailable';
            atcBtn.dataset.id    = '';
            atcBtn.dataset.price = '';
            self.updateCardPrice(productCard, null);
            console.warn('BundleBuilder: product has no variants, card skipped', productCard);
            return;
        }

        this._ensurePriceStyles();
        this.updateSoldOutSwatches(productCard, variants);

        if (!activeColorSwatch && !activeSizeSwatch) {
            if (variants.length !== 1) {
                atcBtn.disabled = true;
                atcBtn.classList.add('disabled');
                atcBtn.textContent = 'Select options';
                return;
            } else {
                updateATCButton(atcBtn, variants[0]);
                this._updateRecipeCardImage(productCard, variants[0], productData);
                return;
            }
        }

        const colorValue       = activeColorSwatch?.dataset?.value       || null;
        const colorOptionIndex = activeColorSwatch?.dataset?.optionIndex || '';
        const sizeValue        = activeSizeSwatch?.dataset?.value        || null;
        const sizeOptionIndex  = activeSizeSwatch?.dataset?.optionIndex  || '';

        const variant = variants.find(v =>
            (!colorOptionIndex || v[`option${colorOptionIndex}`] === colorValue) &&
            (!sizeOptionIndex  || v[`option${sizeOptionIndex}`]  === sizeValue)
        );

        // Tab card: slider image update
        const productSlider = productCard.querySelector('.product-slider');
        if (variant && productSlider) {
            const productCardImages = [...productCard.querySelectorAll('.popup-img')];
            const variantImageIndex = productCardImages.findIndex(
                img => img.dataset.variants && img.dataset.variants.includes(variant.id)
            );
            if (variantImageIndex !== -1) this.moveSliderTo(productSlider, variantImageIndex);
        }

        // Recipe card: featured image update
        this._updateRecipeCardImage(productCard, variant, productData);

        updateATCButton(atcBtn, variant);
    }

    // Helper — recipe card image update
    _updateRecipeCardImage(productCard, variant, productData) {
        const recipeImg = productCard.querySelector('.recipe-made-with__img');
        if (!recipeImg || !variant) return;

        if (variant.featured_image && variant.featured_image.src) {
            recipeImg.src = variant.featured_image.src;
            return;
        }

        if (productData.images && productData.images.length) {
            const matched = productData.images.find(
                img => img.variant_ids && img.variant_ids.includes(variant.id)
            );
            if (matched) recipeImg.src = matched.src;
        }
    }

    /* ============================================================
       PRODUCT CARD CLICK DELEGATION
    ============================================================ */
    initializeProductCardListeners() {
        this.addEventListener('click', e => {

            // Color swatch
            if (e.target.classList.contains('color-swatch')) {
                const group = e.target.closest('.color-swatches');
                group.querySelectorAll('.color-swatch').forEach(s => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            // Size swatch
            if (e.target.classList.contains('size-swatch')) {
                const group = e.target.closest('.size-swatches');
                group.querySelectorAll('.size-swatch').forEach(s => s.classList.remove('active'));
                e.target.classList.add('active');
            }

            // Plus qty
            if (e.target.classList.contains('plus')) {
                const quantity = e.target.closest('.qty').querySelector('input');
                quantity.value = parseInt(quantity.value) + 1;
            }

            // Minus qty
            if (e.target.classList.contains('minus')) {
                const productCard = e.target.closest('.product-card');
                const quantity    = productCard.querySelector('input[type="number"]');
                let   currentQty  = parseInt(quantity.value);

                if (currentQty > 0) {
                    quantity.value = currentQty - 1;

                    const variantId = productCard.querySelector('.add-btn').dataset.id;
                    const existsInBundle = this.selectedProducts.some(
                        p => String(p.id) === String(variantId)
                    );

                    if (existsInBundle) {
                        this.dispatchEvent(new CustomEvent('bundle:remove', {
                            detail: { variantId }
                        }));
                    }
                }
            }

            // Re-check variant on swatch change
            if (e.target.classList.contains('color-swatch') || e.target.classList.contains('size-swatch')) {
                this.onProductChange(e.target.closest('.product-card'));
            }

            // ADD button
            if (e.target.classList.contains('add-btn')) {
                const variantId    = e.target.dataset.id;
                const variantPrice = e.target.dataset.price;
                const productCard  = e.target.closest('.product-card');

                let variantImage = null;
                const popupImgs  = [...productCard.querySelectorAll('.popup-img')];

                if (popupImgs.length) {
                    variantImage = popupImgs.find(img => img.dataset.variants && img.dataset.variants.includes(variantId)) || popupImgs[0];
                }

                if (!variantImage) {
                    variantImage = productCard.querySelector('.recipe-made-with__img');
                }

                const imageSrc      = variantImage ? variantImage.src : '';
                const quantityInput = productCard.querySelector('input[type="number"]');
                const quantity      = quantityInput ? quantityInput.value : 1;

                if (variantId) {
                    this.dispatchEvent(new CustomEvent('bundle:add', {
                        detail: {
                            variantId : variantId,
                            count     : +quantity,
                            image     : imageSrc,
                            price     : variantPrice
                        }
                    }));
                }
            }
        });
    }

    /* ============================================================
       POPUP IMAGE CAROUSEL
    ============================================================ */
    initializePopupCarousel() {
        let slides  = [];
        let current = 0;

        function setupCarouselControls(e) {
            const popup = document.getElementById('global-popup');
            if (!popup || !slides.length) return;

            if (e.target.closest('.close-popup')) {
                popup.classList.remove('active');
            }
            if (e.target.closest('.next')) {
                slides[current].classList.remove('active');
                current = (current + 1) % slides.length;
                slides[current].classList.add('active');
            }
            if (e.target.closest('.prev')) {
                slides[current].classList.remove('active');
                current = (current - 1 + slides.length) % slides.length;
                slides[current].classList.add('active');
            }
        }

        function handlePopupImage(e) {
            const img = e.target.closest('.popup-img');
            if (!img) return;

            if (e.target.closest('#global-popup')) return;

            const popup     = document.getElementById('global-popup');
            const container = popup.querySelector('.popup-images');
            const product   = img.closest('.product-card');
            if (!product) return;

            const imgs = product.querySelectorAll('.popup-img');
            container.innerHTML = '';
            slides  = [];
            current = 0;

            imgs.forEach((i, index) => {
                const clone = i.cloneNode(true);
                clone.classList.add('popup-slide');

                if (i === img) {
                    clone.classList.add('active');
                    current = index;
                }

                container.appendChild(clone);
                slides.push(clone);
            });
 
            popup.classList.add('active');
        }

        document.addEventListener('click', e => {
            handlePopupImage(e);
            setupCarouselControls(e);
        });
    }
}

customElements.define('bundle-builder', BundleBuilder);